import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ArrowPathIcon, FaceSmileIcon, PaperAirplaneIcon } from '@heroicons/react/24/outline';
import { ecosystemService } from '../services/ecosystemService';
import { renderChatMarkdown } from '../utility/renderMarkdownText';
import { useBranding } from '../context/BrandingContext';
import { PoweredBy } from '../components/ui/BrandFooter';
import { cssColor } from '../theme/engine';
import { Button } from '../components/ui/Button';
import { TextArea } from '../components/ui/Field';

/**
 * Ecosystem widgets have no theme settings of their own, so they use the
 * client's (or product's) theme. With no theme set these are the colours the
 * widget has always had.
 */
const DEFAULT_THEME = {
  userMsgBg: `linear-gradient(90deg, ${cssColor('abcad9')} 0%, ${cssColor('5484c5')} 100%)`,
  userMsgFont: '#ffffff',
  botMsgBg: '#ffffff',
  botMsgFont: '#000000',
  sendBtn: cssColor('3b82f6'),
  bg: `linear-gradient(180deg, ${cssColor('f5f7ff')} 0%, ${cssColor('e3ebff')} 42%, ${cssColor('eef6ff')} 100%)`,
  inputTextBg: '#ffffff',
  inputTextFont: '#000000',
  inputBorder: cssColor('e5e7eb'),
  headerBg: `linear-gradient(90deg, ${cssColor('abcad9')} 0%, ${cssColor('5484c5')} 100%)`,
  headerTextFont: '#ffffff',
  placeholder: 'Type your message...',
};

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  thinking?: boolean;
}

const ThinkingDots: React.FC = () => (
  <div className="flex gap-1">
    <span className="animate-bounce" style={{ animationDelay: '0ms' }}>●</span>
    <span className="animate-bounce" style={{ animationDelay: '150ms' }}>●</span>
    <span className="animate-bounce" style={{ animationDelay: '300ms' }}>●</span>
  </div>
);

const PublicEcosystemChatWidget: React.FC = () => {
  const { branding, showTenant } = useBranding();
  const { ecosystemId, clientId } = useParams<{ ecosystemId: string; clientId: string }>();
  const [ecosystem, setEcosystem] = useState<any>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [conversationId, setConversationId] = useState<string | undefined>();
  const [error, setError] = useState<string | null>(null);

  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [userId] = useState<string>(() => clientId || `guest-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`);

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (container) container.scrollTop = container.scrollHeight;
  }, [messages]);

  useEffect(() => {
    if (!ecosystemId) {
      setError('Ecosystem ID is required');
      setIsLoading(false);
      return;
    }
    loadEcosystem();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ecosystemId]);

  useEffect(() => {
    if (!sending && textareaRef.current) textareaRef.current.focus();
  }, [sending]);

  // Visitors are not signed in, so branding comes from the ecosystem's client.
  useEffect(() => {
    const tenantId = ecosystem?.tenantId || null;
    if (tenantId) showTenant(tenantId);
    return () => showTenant(null);
  }, [ecosystem?.tenantId, showTenant]);

  const loadEcosystem = async () => {
    try {
      setIsLoading(true);
      const eco = await ecosystemService.getById(ecosystemId!);
      setEcosystem(eco);
      setMessages([{
        id: 'welcome',
        role: 'assistant',
        content: `Hi! I'm ${eco.name}. How can I help you today?`,
      }]);
    } catch {
      setError('Failed to load chat configuration');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value);
    const ta = textareaRef.current;
    if (ta) {
      ta.style.height = 'auto';
      ta.style.height = `${Math.min(ta.scrollHeight, 120)}px`;
    }
  };

  const send = async () => {
    const msg = input.trim();
    if (!msg || sending || !ecosystemId) return;
    setInput('');
    if (textareaRef.current) textareaRef.current.style.height = 'auto';

    const userMsg: ChatMessage = { id: `u-${Date.now()}`, role: 'user', content: msg };
    const placeholderId = `ph-${Date.now()}`;
    const placeholder: ChatMessage = { id: placeholderId, role: 'assistant', content: '', thinking: true };

    setMessages((prev: ChatMessage[]) => [...prev, userMsg, placeholder]);
    setSending(true);

    try {
      const response = await ecosystemService.sendMessageStream({
        ecosystemId,
        userId,
        message: msg,
        conversationId,
        userTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });

      const reader = response.body?.getReader();
      if (!reader) throw new Error('No response body');

      const decoder = new TextDecoder();
      let buffer = '';
      let accumulated = '';
      let hasFirst = false;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split('\n\n');
        buffer = parts[parts.length - 1];
        for (let i = 0; i < parts.length - 1; i++) {
          const line = parts[i].trim();
          if (!line.startsWith('data: ')) continue;
          try {
            const data = JSON.parse(line.slice(6));
            if (data.type === 'content') {
              if (!hasFirst) { hasFirst = true; accumulated = ''; }
              accumulated += data.content;
              setMessages((prev: ChatMessage[]) => prev.map((m: ChatMessage) =>
                m.id === placeholderId ? { ...m, content: accumulated, thinking: false } : m
              ));
            } else if (data.type === 'complete') {
              const finalContent = (data.content || accumulated) as string;
              setMessages((prev: ChatMessage[]) => prev.map((m: ChatMessage) =>
                m.id === placeholderId ? { ...m, content: finalContent, thinking: false } : m
              ));
              if (data.conversationId) setConversationId(data.conversationId);
            } else if (data.conversationId && !conversationId) {
              setConversationId(data.conversationId);
            }
          } catch { /* skip malformed chunk */ }
        }
      }

      if (!accumulated && buffer.trim()) {
        try {
          const fallback = JSON.parse(buffer.trim());
          const text = (fallback?.message || fallback?.content || fallback?.reply || '') as string;
          if (text) {
            setMessages((prev: ChatMessage[]) => prev.map((m: ChatMessage) =>
              m.id === placeholderId ? { ...m, content: text, thinking: false } : m
            ));
            if (fallback?.conversationId) setConversationId(fallback.conversationId);
          }
        } catch { /* not JSON */ }
      }

      if (!accumulated) {
        setMessages((prev: ChatMessage[]) => prev.map((m: ChatMessage) =>
          m.id === placeholderId && m.thinking ? { ...m, content: 'No response received.', thinking: false } : m
        ));
      }
    } catch (e: unknown) {
      const msg2 = e instanceof Error ? e.message : 'Failed to send message';
      setMessages((prev: ChatMessage[]) => prev.map((m: ChatMessage) =>
        m.id === placeholderId ? { ...m, content: `Error: ${msg2}`, thinking: false } : m
      ));
    } finally {
      setSending(false);
    }
  };

  const handleReset = () => {
    setMessages([{
      id: 'welcome',
      role: 'assistant',
      content: ecosystem ? `Hi! I'm ${ecosystem.name}. How can I help you today?` : 'Hi! How can I help you today?',
    }]);
    setConversationId(undefined);
    setInput('');
  };

  if (isLoading) {
    return (
      <div className="h-screen w-full flex items-center justify-center" style={{ background: DEFAULT_THEME.bg }}>
        <div className="text-sm text-gray-600">Loading chat...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-screen w-full flex items-center justify-center" style={{ background: DEFAULT_THEME.bg }}>
        <div className="text-sm text-red-600">{error}</div>
      </div>
    );
  }

  return (
    <div
      className="flex flex-col h-screen"
      style={{ background: DEFAULT_THEME.bg, fontFamily: branding.fontFamily ? `'${branding.fontFamily}', Inter, sans-serif` : 'Inter, sans-serif' }}
    >
      {/* Header */}
      <div
        className="h-14 flex items-center justify-between px-4 shadow-sm flex-shrink-0"
        style={{ background: DEFAULT_THEME.headerBg, color: DEFAULT_THEME.headerTextFont }}
      >
        <span className="flex items-center gap-2 font-semibold text-base">
          {branding.logoLightUrl && (
            <img src={branding.logoLightUrl} alt="" className="h-6 max-w-[96px] rounded-full bg-white object-contain p-0.5" />
          )}
          {ecosystem?.name || 'Chat Support'}
        </span>
        <Button tone="custom"
          onClick={handleReset}
          className="p-1.5 rounded-lg opacity-70 hover:opacity-100 transition-opacity"
          style={{ color: DEFAULT_THEME.headerTextFont }}
          title="Reset conversation"
        >
          <ArrowPathIcon className="h-4 w-4" />
        </Button>
      </div>

      {/* Messages */}
      <div ref={messagesContainerRef} className="flex-1 overflow-y-auto p-4 space-y-3">
        {messages.map((m: ChatMessage) => {
          const isUser = m.role === 'user';
          return (
            <div key={m.id} className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
              <div
                className="max-w-[80%] rounded-xl px-4 py-2.5 text-sm shadow-sm"
                style={
                  isUser
                    ? { background: DEFAULT_THEME.userMsgBg, color: DEFAULT_THEME.userMsgFont }
                    : { background: DEFAULT_THEME.botMsgBg, color: DEFAULT_THEME.botMsgFont, border: '1px solid #e5e7eb' }
                }
              >
                {m.thinking ? (
                  <ThinkingDots />
                ) : isUser ? (
                  m.content
                ) : (
                  renderChatMarkdown(m.content)
                )}
              </div>
            </div>
          );
        })}
        <div />
      </div>

      {/* Input Area */}
      <div className="flex-shrink-0 p-4 border-t" style={{ borderColor: DEFAULT_THEME.inputBorder }}>
        <div
          className="flex items-center gap-2 rounded-xl px-3 py-2 shadow-sm"
          style={{
            background: DEFAULT_THEME.inputTextBg,
            border: `1px solid ${DEFAULT_THEME.inputBorder}`,
          }}
        >
          <Button tone="custom"
            type="button"
            className="p-1 flex-shrink-0 opacity-50"
            aria-label="Emoji"
            disabled
          >
            <FaceSmileIcon className="h-5 w-5" style={{ color: DEFAULT_THEME.botMsgFont }} />
          </Button>
          <TextArea tone="transparent"
            ref={textareaRef}
            value={input}
            onChange={handleInput}
            rows={1}
            placeholder={DEFAULT_THEME.placeholder}
            disabled={sending}
            className="flex-1 resize-none overflow-auto outline-none focus:border-none focus:outline-none focus:ring-0 placeholder:text-sm disabled:opacity-60 disabled:cursor-not-allowed"
            style={{ maxHeight: '120px', color: DEFAULT_THEME.inputTextFont }}
            onKeyDown={(e: React.KeyboardEvent<HTMLTextAreaElement>) => {
              if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
            }}
          />
          <Button tone="custom"
            onClick={send}
            disabled={sending || !input.trim()}
            className="p-2 rounded-lg transition-opacity disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0"
            style={{ background: DEFAULT_THEME.sendBtn, color: '#ffffff' }}
            aria-label="Send message"
          >
            <PaperAirplaneIcon className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Branding */}
      <div
        className="flex-shrink-0 px-4 pb-2 text-center text-xs opacity-60"
        style={{ color: DEFAULT_THEME.botMsgFont }}
      >
        <PoweredBy />
      </div>
    </div>
  );
};

export default PublicEcosystemChatWidget;
